import { useRef, useState } from "react";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import EUROPEAN_COUNTRIES from "@/lib/europeanCountries";
import { UserRound, Save, Loader2, Bell, BellOff, Upload, Camera } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    country: user?.country || "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data.user);
      toast.success("Profil sačuvan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl space-y-6" data-testid="profile-page">
      <div className="flex justify-end mb-4"><BackButton /></div>
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-zinc-500">Podaci o korisniku</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-wide mt-1">
          Moj <span className="text-neon-green">profil</span>
        </h1>
      </div>

      <div className="nasa-card p-6 md:p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-sm bg-[#22ff77]/10 border border-[#22ff77]/40 overflow-hidden flex items-center justify-center text-3xl font-display font-bold text-[#22ff77]">
            {user?.picture ? (
              <img src={user.picture} alt={user?.name || "avatar"} data-testid="profile-header-avatar" className="w-full h-full object-cover" />
            ) : (
              <UserRound className="w-7 h-7" strokeWidth={1.5} />
            )}
          </div>
          <div>
            <div className="font-display text-lg">{user?.name}</div>
            <div className="text-xs text-zinc-500">{user?.email}</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Ime i prezime">
            <input data-testid="profile-name-input" value={form.name} onChange={set("name")} className="nasa-input" />
          </Field>
          <Field label="Email (ne može se menjati)">
            <input value={user?.email || ""} disabled className="nasa-input opacity-60" />
          </Field>
          <Field label="Telefon">
            <input data-testid="profile-phone-input" value={form.phone} onChange={set("phone")} className="nasa-input" />
          </Field>
          <Field label="Država">
            <select
              data-testid="profile-country-input"
              value={form.country}
              onChange={set("country")}
              className="nasa-input"
            >
              <option value="">— izaberi državu —</option>
              {EUROPEAN_COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Status učešća">
            <input value={user?.investment_status || ""} disabled className="nasa-input opacity-60 uppercase" />
          </Field>
          <Field label="Saldo">
            <input value={`€ ${(user?.balance ?? 0).toFixed(2)}`} disabled className="nasa-input opacity-60 text-[#22ff77]" />
          </Field>

          <div className="sm:col-span-2 pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="save-profile-button"
              className="flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3 px-8 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sačuvaj izmene
            </button>
          </div>
        </form>
      </div>

      <AvatarCard />

      <NotificationsCard />

      <style>{`
        .nasa-input {
          width: 100%;
          background: #000;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.75rem;
          font-size: 0.875rem;
          border-radius: 2px;
          color: #fff;
          font-family: "JetBrains Mono", monospace;
        }
        .nasa-input:focus { outline: none; border-color: #22ff77; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</label>
      {children}
    </div>
  );
}

// Predefined avatar set — 2 carefully designed cartoon avatars (Opcija 1 & Opcija 2).
// Custom upload lets the user provide their own image (resized client-side, stored as data URL).
const PREDEFINED_AVATARS = [
  { key: "option1", url: "/avatars/option1.png" },
  { key: "option2", url: "/avatars/option2.png" },
];

function AvatarCard() {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const saveAvatar = async (picture) => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/avatar", { picture });
      setUser(data.user);
      toast.success("Avatar sačuvan");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Fajl mora biti slika");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Resize to 256x256 and compress to JPEG ~0.8 to stay under ~300KB
        const canvas = document.createElement("canvas");
        const size = 256;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        // cover-fit
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        saveAvatar(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const current = user?.picture || "";

  return (
    <div className="nasa-card p-6 md:p-8" data-testid="avatar-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-4">
        <Camera className="w-3 h-3 text-[#22ff77]" /> Profilna slika
      </div>
      <div className="flex items-start gap-6 flex-wrap">
        <div className="w-24 h-24 rounded-sm border border-[#22ff77]/40 bg-[#22ff77]/5 overflow-hidden flex items-center justify-center shrink-0">
          {current ? (
            <img src={current} alt="Vaš avatar" data-testid="current-avatar" className="w-full h-full object-cover" />
          ) : (
            <UserRound className="w-10 h-10 text-zinc-500" />
          )}
        </div>
        <div className="flex-1 min-w-0 w-full">
          <p className="text-xs text-zinc-400 mb-4">
            Izaberite jedan od ponuđenih avatara ili otpremite svoju sliku.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {PREDEFINED_AVATARS.map((a, i) => {
              const selected = current === a.url;
              return (
                <button
                  key={a.key}
                  type="button"
                  data-testid={`avatar-${a.key}`}
                  disabled={saving}
                  onClick={() => saveAvatar(a.url)}
                  className={`relative p-2 rounded-sm border transition-all bg-black ${selected ? "border-[#22ff77] glow-green" : "border-white/10 hover:border-[#22ff77]/40"}`}
                >
                  <img src={a.url} alt={`Avatar opcija ${i + 1}`} className="w-full aspect-square object-cover rounded-sm bg-black" />
                </button>
              );
            })}

            <button
              type="button"
              data-testid="avatar-upload-button"
              disabled={saving}
              onClick={() => fileRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 p-2 rounded-sm border border-dashed border-[#ff7a18]/50 hover:border-[#ff7a18] hover:bg-[#ff7a18]/5 transition-all min-h-[120px]"
            >
              {saving ? (
                <Loader2 className="w-7 h-7 text-[#ff7a18] animate-spin" />
              ) : (
                <Upload className="w-7 h-7 text-[#ff7a18]" />
              )}
              <div className="text-[10px] uppercase tracking-widest text-[#ff7a18] text-center leading-tight">
                Učitaj sliku
              </div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationsCard() {
  const { user, setUser } = useAuth();
  const [enabled, setEnabled] = useState(user?.notify_cycle_alerts !== false);
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      const { data } = await api.put("/auth/notifications", { notify_cycle_alerts: next });
      setUser(data.user);
      toast.success(next ? "Obaveštenja uključena" : "Obaveštenja isključena");
    } catch (err) {
      setEnabled(!next);
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nasa-card p-6 md:p-8" data-testid="notifications-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-3">
        {enabled ? <Bell className="w-3 h-3 text-[#22ff77]" /> : <BellOff className="w-3 h-3 text-zinc-500" />}
        Obaveštenja
      </div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-wide">
            FOMO email kad se ciklus zatvara
          </h2>
          <p className="text-xs text-zinc-400 mt-1 max-w-lg">
            Kada aktivni ciklus dostigne <span className="text-[#22ff77]">90%</span>, automatski vam šaljemo email
            sa tačnim brojem preostalih tiketa do izvlačenja mašine.
          </p>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          data-testid="notifications-toggle"
          aria-pressed={enabled}
          className={`relative w-16 h-8 rounded-full transition-all ${enabled ? "bg-[#22ff77]" : "bg-zinc-800 border border-white/10"} disabled:opacity-60`}
        >
          <span
            className={`absolute top-0.5 w-7 h-7 rounded-full bg-black transition-all ${enabled ? "left-[34px]" : "left-0.5"}`}
            style={{ boxShadow: enabled ? "0 0 12px rgba(34,255,119,0.6)" : "none" }}
          />
        </button>
      </div>
    </div>
  );
}
